import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { formatDate, formatMonthDay } from "@satyrsmc/shared/lib/date-utils";

/**
 * Buckets a list of registration timestamps into daily counts, from the
 * earliest date through today (so trailing zero days show up rather than
 * skipping straight to "today" and looking artificially flat).
 */
export function buildDailyBuckets(dates: (string | null)[]): {
  categories: string[];
  counts: number[];
} {
  const valid = dates.filter((d): d is string => d != null).map((d) => d.slice(0, 10));
  if (valid.length === 0) return { categories: [], counts: [] };

  const min = valid.reduce((a, b) => (a < b ? a : b));
  const today = new Date().toISOString().slice(0, 10);

  const buckets: Record<string, number> = {};
  let cursor = new Date(min + "T12:00:00Z");
  const end = new Date(today + "T12:00:00Z");
  while (cursor <= end) {
    buckets[cursor.toISOString().slice(0, 10)] = 0;
    cursor = new Date(cursor.getTime() + 86400000);
  }
  for (const d of valid) {
    if (d in buckets) buckets[d]++;
  }

  const categories = Object.keys(buckets).sort();
  const counts = categories.map((k) => buckets[k]);
  return { categories, counts };
}

interface RegistrationsOverTimeChartProps {
  categories: string[];
  counts: number[];
  height?: number;
}

/** The "Registrations over time" area chart shared by the single-event attendees
 * card and the dashboard's aggregated Badger-events widget. */
export function RegistrationsOverTimeChart({
  categories,
  counts,
  height = 160,
}: RegistrationsOverTimeChartProps) {
  const chartOptions: ApexOptions = {
    chart: {
      type: "area",
      toolbar: { show: false },
      fontFamily: "inherit",
      background: "transparent",
      sparkline: { enabled: false },
    },
    stroke: { curve: "smooth", width: 2 },
    fill: { type: "gradient", gradient: { opacityFrom: 0.3, opacityTo: 0.05 } },
    colors: ["#3b82f6"],
    dataLabels: { enabled: false },
    xaxis: {
      categories,
      labels: {
        rotate: -45,
        formatter: (v: string) => formatMonthDay(v),
        style: { fontSize: "10px" },
      },
      tickAmount: Math.min(categories.length, 8),
    },
    yaxis: {
      labels: { style: { fontSize: "10px" } },
      min: 0,
      forceNiceScale: true,
    },
    grid: { borderColor: "rgba(0,0,0,0.08)", strokeDashArray: 4 },
    tooltip: {
      x: {
        formatter: (v: number) => {
          const label = categories[v - 1];
          if (!label) return "";
          return formatDate(label);
        },
      },
    },
  };

  const chartSeries = [{ name: "Registrations", data: counts }];

  return <Chart options={chartOptions} series={chartSeries} type="area" height={height} />;
}
